import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE, toSupported } from '@/i18n/language';

/**
 * Get a localized field value from an object with fallback logic.
 *
 * Database fields follow the pattern: field_name_en, field_name_fr and
 * field_name_nl — one column per language the app ships.
 *
 * Fallback strategy:
 * 1. Try the field for the player's language (e.g., title_nl)
 * 2. If that value is an empty string, null, undefined, or whitespace-only,
 *    fall back to English — the app's fallback language, so a question Max has
 *    not translated yet reads in English rather than disappearing
 * 3. Return an empty string when even English is missing
 *
 * @param obj - The object containing the localized fields
 * @param fieldBase - The base field name without the locale suffix (e.g., 'title' for 'title_en'/'title_nl')
 * @param locale - The locale code; region tags like 'nl-BE' are accepted
 * @returns The localized field value or English fallback
 *
 * @example
 * const question = { title_en: 'Who will win?', title_nl: '' };
 * getLocalizedField(question, 'title', 'nl') // Returns 'Who will win?' (fallback to English)
 *
 * const question2 = { title_en: 'Who will win?', title_nl: 'Wie gaat er winnen?' };
 * getLocalizedField(question2, 'title', 'nl') // Returns 'Wie gaat er winnen?'
 */
export function getLocalizedField<T extends Record<string, any>>(
  obj: T,
  fieldBase: string,
  locale: string
): string {
  const englishField = `${fieldBase}_${DEFAULT_LANGUAGE}`;
  const language = toSupported(locale) ?? DEFAULT_LANGUAGE;

  if (language !== DEFAULT_LANGUAGE) {
    const translated = obj[`${fieldBase}_${language}`];

    // Only use the translation when it actually says something.
    if (typeof translated === 'string' && translated.trim() !== '') {
      return translated;
    }
  }

  return obj[englishField] || '';
}

/**
 * Custom hook to get a localized field getter function based on the current i18n language.
 *
 * @returns A function that takes an object and field base and returns the localized value
 *
 * @example
 * import { useLocalizedFieldGetter } from '@/utils/localized-field';
 *
 * const getField = useLocalizedFieldGetter();
 * const questionText = getField(question, 'title');
 */
export function useLocalizedFieldGetter() {
  const { i18n } = useTranslation();

  return (obj: Record<string, any>, fieldBase: string) => {
    return getLocalizedField(obj, fieldBase, i18n.language);
  };
}
